import React, { useEffect, useMemo, useRef } from 'react';

import {
  useAccountCompanyId,
  useAccountCompanyIds,
  useAccountIsReadyToUseLK,
  useAccountUserId,
} from 'application/hooks';
import { useApolloClient } from '@apollo/client';
import { useAppSelector } from 'application/providers/StoreProvider';
import { selectIsAuthorized } from 'application/store';

import {
  usePointList,
  usePointsCertificate,
  useTimezones,
  usePoint,
  usePointCreate,
  usePointUpdate,
  usePointRemove,
  usePointSetAds,
  usePointSetCompanyTags,
  usePointSleepSettings,
  usePointChangeDevice,
  usePointBroadcastHistory,
  usePointBroadcastHistoryReportCreate,
  PointCreateRequestResultType,
} from '@entities/point';

import {
  PointsPlayingStatusesPureType,
  PointQueryFilter,
  PointQueryPagination,
  PointQuerySort,
  SmartlkPointCreateMutationInput,
  PointPureUpdateMutationInput,
  PointPureSleepSettingsMutationInput,
  PointSwitchDeviceMutationInput,
  CertificatePointsQueryFilter,
  PointPureSetAdCampaignsMutationInput,
  PointListDocument,
  PointListQuery,
  PointsQueryPaginationType,
} from '@shared/api/graphql/generated';

import { PointModelContextProps, PointModelContextType } from './types';
import { ALL_POINT_STATUSES_COUNT, DEFAULT_FILTERS, DEFAULT_PAGINATION, DEFAULT_PAGINATION_INFO } from './consts';

type PointModelPaginationParams = {
  filters: PointQueryFilter;
  pagination: PointQueryPagination;
  sort: PointQuerySort;
};

const PointModelContext = React.createContext<PointModelContextType>({} as PointModelContextType);

export const usePointModelContext = () => React.useContext(PointModelContext);

const PointModelProvider: React.FC<PointModelContextProps> = ({
  children,
  pageSize = DEFAULT_PAGINATION.pageSize,
  isVideoDemo,
}) => {
  const isReadyToUseLK = useAccountIsReadyToUseLK();
  const companyIds = useAccountCompanyIds();
  const companyId = useAccountCompanyId();
  const currentUserId = useAccountUserId();
  const isAuthorized = useAppSelector(selectIsAuthorized);

  const client = useApolloClient();

  const [pointsRequest, pointsResult] = usePointList();
  const [, pointsCertificateResult] = usePointsCertificate();

  const { timezones, request: getTimezones } = useTimezones(!isAuthorized);
  const { loading: pointLoading, point, getPoint } = usePoint();

  const { request: createPointRepository, loading: createPointLoading, error: createPointError } = usePointCreate();
  const { request: updatePointRepository, loading: updatePointLoading, error: updatePointError } = usePointUpdate();
  const { request: removePointRepository, loading: removePointLoading, error: removePointError } = usePointRemove();
  const { request: setAdCampaigns, loading: pointAdsUpdateLoading, error: pointAdsUpdateError } = usePointSetAds();
  const [setCompanyTags] = usePointSetCompanyTags();
  const { request: setSleepSettings, loading: updatePointSleepSettingsLoading } = usePointSleepSettings();
  const {
    request: setDevice,
    loading: updateDeviceLoading,
    error: updateDeviceError,
    point: updateDevicePoint,
  } = usePointChangeDevice();
  const pointBroadcastHistory = usePointBroadcastHistory();
  const {
    request: getPointHistoryReport,
    loading: pointHistoryReportCreateLoading,
    error: pointHistoryReportCreateError,
  } = usePointBroadcastHistoryReportCreate();

  const getPoints = async ({ filters, pagination, sort }: PointModelPaginationParams) => {
    await pointsRequest(filters, pagination, sort);
  };

  const readPoints = ({ filters, pagination, sort }: PointModelPaginationParams) => {
    const result = client.readQuery<PointListQuery>({
      query: PointListDocument,
      variables: { filters, pagination, sort },
    });

    return (result?.pointsPaginationQuery as PointsQueryPaginationType) || null;
  };

  const getMorePoints = async ({ filters, pagination, sort }: PointModelPaginationParams) => {
    await pointsResult.loadMore(filters, pagination, sort);
  };

  // @ts-ignore
  const filtersRef = useRef<PointQueryFilter | CertificatePointsQueryFilter>({
    ...DEFAULT_FILTERS,
    companyIds,
    isVideoDemo,
  });

  const paginationRef = useRef<PointQueryPagination>({
    page: DEFAULT_PAGINATION.page,
    pageSize,
  });

  const sortRef = useRef<PointQuerySort>(PointQuerySort.IsVideoDemoDesc);
  const allPointsCount = useRef<null | number>(null);

  const cachedPoints = readPoints({
    filters: filtersRef.current,
    pagination: paginationRef.current,
    sort: sortRef.current,
  });

  const initModel = async () => {
    await getTimezones();
    await getPoints({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });
  };

  const resetFilters = () => {
    filtersRef.current = {
      ...DEFAULT_FILTERS,
      companyIds,
      // @ts-ignore
      isVideoDemo,
    };

    paginationRef.current = DEFAULT_PAGINATION;

    sortRef.current = PointQuerySort.IsVideoDemoDesc;
  };

  useEffect(() => () => resetFilters(), []);

  useEffect(() => {
    if (currentUserId && companyId && isReadyToUseLK) {
      initModel();
    }
  }, [currentUserId, companyId, isReadyToUseLK]);

  useEffect(() => {
    if (
      pointsResult.data.paginationInfo?.totalCount !== undefined &&
      (allPointsCount.current === null || allPointsCount.current === 0)
    ) {
      allPointsCount.current = pointsResult.data.paginationInfo?.totalCount || 0;
    }
  }, [pointsResult, pointsCertificateResult]);

  const requestPoints = async (
    filters: PointQueryFilter = DEFAULT_FILTERS,
    pagination: PointQueryPagination = {
      page: DEFAULT_PAGINATION.page,
      pageSize: pageSize || DEFAULT_PAGINATION.pageSize,
    }
  ) => {
    filtersRef.current = {
      ...filtersRef.current,
      ...filters,
      companyIds,
      // @ts-ignore
      isVideoDemo,
    };

    paginationRef.current = {
      ...paginationRef.current,
      ...pagination,
    };

    await getPoints({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });
  };

  const readPointsWithDefaultFilters = (
    filters: PointQueryFilter = DEFAULT_FILTERS,
    pagination: PointQueryPagination = {
      page: DEFAULT_PAGINATION.page,
      pageSize: pageSize || DEFAULT_PAGINATION.pageSize,
    }
  ) => {
    filtersRef.current = {
      ...filtersRef.current,
      ...filters,
      companyIds,
      // @ts-ignore
      isVideoDemo,
    };

    paginationRef.current = {
      ...paginationRef.current,
      ...pagination,
    };

    const result = readPoints({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });

    return result;
  };

  const loadMorePoints = async () => {
    const page = (pointsResult.data.paginationInfo?.page || 0) + 1;

    if (page <= paginationRef.current.page) return;

    paginationRef.current.page = page;

    await getMorePoints({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });
  };

  const requestPoint = async (id: string) => {
    await getPoint(id);
  };

  const createPoint = async (input: SmartlkPointCreateMutationInput): Promise<PointCreateRequestResultType> => {
    const result = await createPointRepository(input);

    if (result && result.ok) {
      resetFilters();
      await requestPoints();
    }

    return result;
  };

  const updatePoint = async (input: PointPureUpdateMutationInput) => {
    const isOk = await updatePointRepository(input);
    return isOk;
  };

  const removePoint = async (id: string): Promise<boolean> => {
    const isOk = await removePointRepository(id);

    if (isOk) {
      resetFilters();
    }

    return isOk;
  };

  const pointSetAdCampaigns = async (input: PointPureSetAdCampaignsMutationInput) => {
    const isOk = await setAdCampaigns(input);
    return isOk;
  };

  const pointSetCompanyTags = async (pointId: string, tagNames: string[]) => {
    const isOk = await setCompanyTags({ pointId, tagNames });

    if (isOk) {
      await requestPoints(filtersRef.current);
      await requestPoint(pointId);
    }

    return isOk;
  };

  const pointSetSleepSettings = async (input: PointPureSleepSettingsMutationInput) => {
    const isOk = await setSleepSettings(input);
    return isOk;
  };

  const pointSetDevice = async (input: PointSwitchDeviceMutationInput) => {
    const isOk = await setDevice(input);
    return isOk;
  };

  const requestPointHistory = async (pointId: string, dateFrom: string, dateTo: string) => {
    await pointBroadcastHistory.request(pointId, dateFrom, dateTo);
  };

  const loadMorePointHistory = async (pointId: string, dateFrom: string, dateTo: string) => {
    await pointBroadcastHistory.loadMore(pointId, dateFrom, dateTo);
  };

  const downloadPointHistory = async (pointId: string, reportDate: string) => {
    const result = await getPointHistoryReport(pointId, reportDate);
    return result;
  };

  const preparedPoints = useMemo(
    () =>
      cachedPoints?.result?.map((item) => ({
        id: item.id,
        isEnabled: item.isEnabled,
        device: item.device,
        address: item.address,
        stream: item.stream,
        playingStatus: item.playingStatus,
        comment: item.comment,
        tags: item.tags,
        certificateIsAvailable: item.certificateIsAvailable,
        cutDuration: item.cutDuration,
        withMusicCut: item.withMusicCut,
        videoStream: item.videoStream,
        isVideoMode: item.isVideoMode,
        isVideoDemo: item.isVideoDemo,
      })) || [],
    [cachedPoints]
  );

  const preparedStatusesInfo = useMemo(() => {
    let statusesInfo: PointsPlayingStatusesPureType = {};

    const { data } = pointsResult;

    if (data.statusesInfo) {
      statusesInfo = { ...data.statusesInfo };
      delete statusesInfo.__typename;
    }

    return statusesInfo;
  }, [pointsResult.data.statusesInfo]);

  const preparedTimezones = useMemo(() => {
    if (!timezones) return [];

    return timezones.map((timezone) => ({
      timezone: timezone?.timezone || '',
      title: timezone?.title || '',
    }));
  }, [timezones]);

  const pointsModel: PointModelContextType = {
    points: preparedPoints,
    point,
    pointLoading,
    pointHistory: pointBroadcastHistory.data.history,
    pointHistoryPaginationInfo: pointBroadcastHistory.data.paginationInfo,
    statusesInfo: preparedStatusesInfo,
    paginationInfo: pointsResult.data.paginationInfo || DEFAULT_PAGINATION_INFO,
    certificatePointsCount: pointsCertificateResult?.certificatePointsCount,
    filters: filtersRef.current,
    timezones: preparedTimezones,
    pointsLoading: pointsResult.loading,
    createPointLoading,
    createPointError,
    updatePointLoading,
    updatePointError,
    removePointLoading,
    removePointError,
    updatePointSleepSettingsLoading,
    updateDeviceLoading,
    updateDeviceError,
    updatedPointDevice: updateDevicePoint?.device || undefined,
    pointHistoryLoading: pointBroadcastHistory.loading,
    pointHistoryReportCreateLoading,
    pointHistoryReportCreateError,
    pointAdsUpdateLoading,
    pointAdsUpdateError,
    pointsCalled: pointsResult.called,
    allPointsCount: allPointsCount.current,
    allPointStatusesCount: ALL_POINT_STATUSES_COUNT,
    requestPoints,
    readPoints: readPointsWithDefaultFilters,
    loadMorePoints,
    requestPoint,
    createPoint,
    updatePoint,
    removePoint,
    pointSetAdCampaigns,
    pointSetCompanyTags,
    pointSetSleepSettings,
    pointSetDevice,
    requestPointHistory,
    loadMorePointHistory,
    downloadPointHistory,
    resetFilters,
  };

  return <PointModelContext.Provider value={pointsModel}>{children}</PointModelContext.Provider>;
};

export default PointModelProvider;
